/*
  rw-physics.js: iOS-style motion for the Real World storefront. Physics only,
  no visual changes: every element keeps its look, only how it moves changes.

  Two ideas, both from UIKit:

  1. Rubber band. Dragging past an edge follows the finger less and less:
       offset = (1 - 1 / (c * x / d + 1)) * d       c = 0.55, d = the dimension
     It is a hyperbola with a ceiling, so you can never pull past one screen.

  2. Springs instead of timed tweens. A damped spring (m*x'' = -k*x - b*x')
     starts from wherever the thing is and however fast it is moving, so an
     animation grabbed halfway carries on from there instead of restarting.
     Presets match SwiftUI: smooth (no bounce), snappy (a touch), bouncy.
     Released motion runs on the compositor (Web Animations API) at the
     screen's own refresh rate; only finger tracking runs per pointer event.

  Loaded once from rw-header. Exposes window.RWPhysics and wires:
    - the product gallery (rw-main-product calls RWPhysics.gallery)
    - the mobile menu drawer (.rw-drawer / .rw-drawer--open)
    - the cart drawer (.rw-cd / .is-open, panel .rw-cd__panel), which can also
      be pulled out with a right-to-left swipe on any part of the page that
      has no sideways gesture of its own
    - the desktop search shelf height (rw-header calls RWPhysics.height)
  Everything falls back to the existing CSS when reduced motion is on.
*/
(function () {
  if (window.RWPhysics) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var PRESETS = {
    smooth: { response: 0.5, damping: 1 },
    snappy: { response: 0.4, damping: 0.85 },
    bouncy: { response: 0.5, damping: 0.7 }
  };

  function rubber(x, d, c) {
    if (!d) return 0;
    c = c || 0.55;
    var s = x < 0 ? -1 : 1;
    return s * (1 - 1 / (Math.abs(x) * c / d + 1)) * d;
  }

  /* One spring per target. Mass 1; stiffness and damping from response
     (the period of the undamped spring, seconds) and damping ratio. */
  function spring(opts) {
    var p = PRESETS[opts.preset] || PRESETS.smooth;
    var response = opts.response || p.response;
    var zeta = opts.damping != null ? opts.damping : p.damping;
    var k = Math.pow(2 * Math.PI / response, 2);
    var b = 4 * Math.PI * zeta / response;
    var s = { x: opts.from, v: opts.velocity || 0, to: opts.to, raf: 0, done: false };
    var last = 0;

    function finish() {
      s.done = true;
      s.x = s.to; s.v = 0;
      opts.onUpdate(s.x, 0);
      if (opts.onDone) opts.onDone();
    }

    function step(t) {
      if (s.done) return;
      if (!last) last = t;
      var dt = Math.min(0.064, (t - last) / 1000);
      last = t;
      /* fixed substeps keep a stiff spring stable on a slow frame */
      var n = Math.max(1, Math.ceil(dt / 0.004));
      var h = dt / n;
      for (var i = 0; i < n; i++) {
        var a = -k * (s.x - s.to) - b * s.v;
        s.v += a * h;
        s.x += s.v * h;
      }
      var tol = opts.tolerance || 0.5;
      if (Math.abs(s.x - s.to) < tol && Math.abs(s.v) < tol * 10) return finish();
      opts.onUpdate(s.x, s.v);
      s.raf = requestAnimationFrame(step);
    }

    if (reduce) { finish(); }
    else s.raf = requestAnimationFrame(step);

    return {
      stop: function () { s.done = true; cancelAnimationFrame(s.raf); },
      get value() { return s.x; },
      get velocity() { return s.v; },
      retarget: function (to) { s.to = to; }
    };
  }

  /* Velocity from the last ~100ms of pointer samples, px per second. */
  function tracker() {
    var pts = [];
    return {
      add: function (x) {
        var t = performance.now();
        pts.push([x, t]);
        while (pts.length > 2 && t - pts[0][1] > 100) pts.shift();
      },
      velocity: function () {
        if (pts.length < 2) return 0;
        var a = pts[0], z = pts[pts.length - 1];
        /* a finger that has stopped has no speed, however fast it came in */
        if (performance.now() - z[1] > 60) return 0;
        var dt = (z[1] - a[1]) / 1000;
        return dt > 0 ? (z[0] - a[0]) / dt : 0;
      },
      reset: function () { pts = []; }
    };
  }

  function setImp(el, prop, val) {
    if (val === null) el.style.removeProperty(prop);
    else el.style.setProperty(prop, val, 'important');
  }

  /* ── Compositor glide ──
     A main-thread requestAnimationFrame loop tops out at 60fps in iOS Safari
     and stutters whenever another script runs. So the spring is solved ahead
     of time (240 steps a second, sampled at 120) and handed to the Web
     Animations API on the `translate` property, which the GPU plays at the
     screen's full rate. Still interruptible: stop() reads the exact position
     and speed at that instant from the samples. `translate` is used rather
     than `transform` because several of these elements carry a `transform:
     ... !important` in their CSS, and !important beats an animation. */
  var FPS = 120;
  function solve(from, to, v0, preset) {
    var p = PRESETS[preset] || PRESETS.smooth;
    var k = Math.pow(2 * Math.PI / p.response, 2), c = 4 * Math.PI * p.damping / p.response;
    var x = from, v = v0 || 0, out = [x], h = 1 / 240, span = Math.max(1, Math.abs(to - from));
    for (var i = 0; i < 240 * 3; i++) {
      var acc = -k * (x - to) - c * v;
      v += acc * h; x += v * h;
      if (i % 2) out.push(x);
      if (Math.abs(x - to) < Math.max(0.25, span * 0.0005) && Math.abs(v) < 4) break;
    }
    out.push(to);
    return out;
  }
  function px(v) { return v + 'px 0'; }
  /* comp (optional): { el, map } — a second element whose opacity is
     map(position), played on the same curve so a scrim moves with its panel. */
  function glide(el, from, to, v0, preset, onDone, comp) {
    el.style.translate = px(to);
    if (comp) comp.el.style.opacity = String(comp.map(to));
    if (reduce || !el.animate || from === to) { if (onDone) onDone(); return null; }
    var vals = solve(from, to, v0, preset);
    var dur = (vals.length - 1) * 1000 / FPS;
    var anim = el.animate(vals.map(function (v) { return { translate: px(v) }; }), { duration: dur, easing: 'linear' });
    var canim = comp && comp.el.animate ? comp.el.animate(vals.map(function (v) { return { opacity: comp.map(v) }; }), { duration: dur, easing: 'linear' }) : null;
    var stopped = false;
    anim.onfinish = function () { if (!stopped && onDone) onDone(); };
    return {
      stop: function () {
        stopped = true;
        var t = Math.max(0, Math.min(dur, anim.currentTime || 0)) * FPS / 1000;
        var i = Math.min(vals.length - 2, Math.floor(t)), f = t - i;
        var x = vals[i] + (vals[i + 1] - vals[i]) * f;
        var v = (vals[i + 1] - vals[i]) * FPS;
        anim.cancel();
        if (canim) canim.cancel();
        el.style.translate = px(x);
        if (comp) comp.el.style.opacity = String(comp.map(x));
        return { x: x, v: v };
      }
    };
  }

  /* Where a flick coasts to, UIScrollView-style: deceleration 0.998 per ms
     means a release at v px/s travels another v * 0.499 px. Sheets settle to
     whichever side that projected point lands on, so a gentle flick from
     anywhere is enough and a slow drag goes where you left it. */
  function project(x, v) { return x + v * 0.998 / (1 - 0.998) / 1000; }

  /* ── Gallery ──
     The track follows the finger 1:1, rubber-bands past the first and last
     slide, and on release settles with a spring that starts at the finger's
     speed. Arrows and thumbs spring from wherever the track is, so tapping
     mid-flight redirects instead of restarting. */
  function gallery(track, count, onIndex) {
    var viewport = track.parentElement;
    var x = 0, idx = 0, anim = null;
    var width = function () { return viewport.clientWidth || 1; };
    setImp(track, 'transition', 'none');
    setImp(track, 'transform', 'none');
    setImp(track, 'will-change', 'translate');
    setImp(track, 'touch-action', 'pan-y');
    var imgs = track.querySelectorAll('img');
    for (var q = 0; q < imgs.length; q++) imgs[q].draggable = false;

    function halt() { var r = anim ? anim.stop() : null; anim = null; if (r) x = r.x; return r ? r.v : 0; }
    function settle(target, velocity) {
      idx = target;
      onIndex(idx);
      var carried = halt();
      var to = -idx * width();
      anim = glide(track, x, to, velocity != null ? velocity : carried, 'smooth', function () { anim = null; });
      x = to;
    }

    var tr = tracker(), sx = 0, sy = 0, base = 0, mode = null, id = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      id = e.pointerId; sx = e.clientX; sy = e.clientY; mode = null;
      halt();
      base = x; tr.reset(); tr.add(e.clientX);
    });
    track.addEventListener('pointermove', function (e) {
      if (e.pointerId !== id) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (!mode) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        mode = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (mode === 'x') { try { track.setPointerCapture(id); } catch (_) {} }
      }
      if (mode !== 'x') return;
      e.preventDefault();
      tr.add(e.clientX);
      var w = width(), min = -(count - 1) * w, next = base + dx;
      if (next > 0) next = rubber(next, w);
      else if (next < min) next = min + rubber(next - min, w);
      x = next; track.style.translate = px(x);
    });
    function end(e) {
      if (e.pointerId !== id) return;
      id = null;
      if (mode !== 'x') { mode = null; if (x !== -idx * width()) settle(idx, 0); return; }
      mode = null;
      var v = tr.velocity(), w = width();
      /* project where a flick would coast to */
      var projected = x + v * 0.2;
      var target = Math.round(-projected / w);
      target = Math.max(idx - 1, Math.min(idx + 1, target));
      target = Math.max(0, Math.min(count - 1, target));
      settle(target, v);
      /* the swipe is not a click on the slide */
      var swallow = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
      track.addEventListener('click', swallow, true);
      setTimeout(function () { track.removeEventListener('click', swallow, true); }, 350);
    }
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    window.addEventListener('resize', function () { halt(); x = -idx * width(); track.style.translate = px(x); });

    return { go: function (i) { settle(i, null); }, get index() { return idx; } };
  }

  /* ── Side sheet (menu drawer, cart drawer) ──
     Opens and closes on a spring from the current position, and can be
     dragged shut: 1:1 toward closed, rubber band the other way, a flick or
     past 40% closes it by clicking the drawer's own close control so the
     existing code keeps owning state. While it is being driven, the CSS
     transform is pinned to none and position lives on `translate`. */
  function sheet(cfg) {
    var root = cfg.root, panel = cfg.panel;
    if (!root || !panel || panel.__rwSheet) return;
    panel.__rwSheet = true;
    var anim = null, x = null, wasOpen = cfg.isOpen();
    var w = function () { return panel.getBoundingClientRect().width || panel.offsetWidth || 1; };
    var visEl = cfg.visibilityEl || panel;
    var scrim = cfg.scrim || null;
    var comp = scrim ? { el: scrim, map: function (v) { var W = w(); return Math.max(0, Math.min(1, 1 - v / W)); } } : null;

    function take() {
      setImp(panel, 'transition', 'none');
      setImp(panel, 'transform', 'none');
      setImp(visEl, 'visibility', 'visible');
      if (scrim) {
        setImp(scrim, 'transition', 'none');
        if (cfg.scrimDisplay) setImp(scrim, 'display', cfg.scrimDisplay);
      }
    }
    function scrimAt(v) { if (scrim) scrim.style.opacity = String(comp.map(v)); }
    function freeScrim() {
      if (!scrim) return;
      scrim.style.removeProperty('opacity');
      if (cfg.scrimDisplay) setImp(scrim, 'display', null);
      requestAnimationFrame(function () { if (!anim) setImp(scrim, 'transition', null); });
    }
    function release() {
      /* hand back to the CSS closed state without its transition replaying */
      panel.style.removeProperty('translate');
      setImp(panel, 'transform', null);
      setImp(visEl, 'visibility', null);
      freeScrim();
      void panel.offsetWidth;
      requestAnimationFrame(function () { if (!anim) setImp(panel, 'transition', null); });
      x = null;
    }
    function halt() { var r = anim ? anim.stop() : null; anim = null; if (r) x = r.x; return r ? r.v : 0; }
    function run(open, velocity) {
      if (reduce) return;
      var carried = halt();
      if (x === null) x = open ? w() : 0;
      take();
      var to = open ? 0 : w();
      anim = glide(panel, x, to, velocity != null ? velocity : carried, 'smooth', function () {
        anim = null;
        if (!open) release(); else freeScrim();
      }, comp);
      x = to;
      if (!anim) { if (!open) release(); else freeScrim(); }
    }

    var pendingV = null;
    new MutationObserver(function () {
      var open = cfg.isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      var v = pendingV; pendingV = null;
      run(open, v);
    }).observe(cfg.classEl || root, { attributes: true, attributeFilter: ['class'] });

    /* Drag to dismiss. Touch events, not pointer events: the panel body is a
       vertical scroller, and the browser cancels a pointer stream the moment
       it decides the gesture is a scroll, mid-drag, at full speed. Once a
       touch is claimed as sideways it is ours (preventDefault), and it is
       stopped here so older handlers (the cart's own fixed-threshold swipe in
       theme.liquid) never make a second decision on the same drag. */
    var tr = tracker(), sx = 0, sy = 0, base = 0, mode = null, live = false;
    function inHScroller(t) {
      for (var n = t; n && n !== panel; n = n.parentElement) {
        var ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true;
      }
      return false;
    }
    panel.addEventListener('touchstart', function (e) {
      live = false;
      if (reduce || !cfg.isOpen() || e.touches.length !== 1 || inHScroller(e.target)) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; mode = null; live = true;
      tr.reset(); tr.add(sx);
    }, { capture: true, passive: true });
    panel.addEventListener('touchmove', function (e) {
      if (!live) return;
      var t0 = e.touches[0], dx = t0.clientX - sx, dy = t0.clientY - sy;
      if (!mode) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        mode = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
        if (mode !== 'x') { live = false; return; }
        halt();
        if (x === null) x = 0;
        base = x - dx;
        take();
      }
      if (e.cancelable) e.preventDefault();
      e.stopImmediatePropagation();
      tr.add(t0.clientX);
      var next = base + dx;
      if (next < 0) next = rubber(next, w());
      x = next; panel.style.translate = px(x); scrimAt(x);
    }, { capture: true, passive: false });
    function end() {
      if (!live) return;
      live = false;
      if (mode !== 'x') { mode = null; return; }
      mode = null;
      var v = tr.velocity();
      var close = project(x, v) > w() * 0.5;
      if (close) {
        var btn = cfg.closeBtn && cfg.closeBtn();
        wasOpen = false;
        run(false, v);
        if (btn) btn.click();
      } else run(true, v);
    }
    panel.addEventListener('touchend', end, { capture: true, passive: true });
    panel.addEventListener('touchcancel', end, { capture: true, passive: true });

    /* Pull to open: a right-to-left swipe anywhere that has no horizontal
       gesture of its own draws the sheet in under the finger. Anything that
       already swipes sideways (the gallery, Pairs well with, any horizontal
       scroller, sliders, fields, an open menu or search, a modal that has
       locked the page) keeps its gesture. Touch only. */
    if (!cfg.pullOpen) return;
    var BLOCK = 'input,textarea,select,[contenteditable],iframe,video,[role="slider"],' +
      '.rw-product-gallery,.rw-carousel-track,[data-rw-xs-track],.rw-drawer,.rw-search,.rw-cd,[data-no-cart-swipe]';
    function blocked(t) {
      if (!t || t.nodeType !== 1) return true;
      var de = document.documentElement, b = document.body;
      if (getComputedStyle(de).overflow === 'hidden' || getComputedStyle(b).overflow === 'hidden') return true;
      if (b.classList.contains('rw-body--drawer-open') || b.classList.contains('rw-cd-open')) return true;
      if (t.closest(BLOCK)) return true;
      for (var n = t; n && n !== b; n = n.parentElement) {
        var ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true;
      }
      return false;
    }
    var pg = null, ptr = tracker();
    document.addEventListener('touchstart', function (e) {
      pg = null;
      if (reduce || cfg.isOpen() || e.touches.length !== 1) return;
      var t0 = e.touches[0];
      /* leave the screen edge to the browser's own back/forward swipe */
      if (t0.clientX > window.innerWidth - 16 || t0.clientX < 16) return;
      if (blocked(e.target)) return;
      pg = { sx: t0.clientX, sy: t0.clientY, mode: null };
    }, { passive: true });
    document.addEventListener('touchmove', function (e) {
      if (!pg) return;
      var t0 = e.touches[0], dx = t0.clientX - pg.sx, dy = t0.clientY - pg.sy;
      if (!pg.mode) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (dx < 0 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          pg.mode = 'pull';
          halt();
          take();
          x = w();
          ptr.reset();
          pg.sx = t0.clientX;
        } else { pg = null; return; }
      }
      if (e.cancelable) e.preventDefault();
      ptr.add(t0.clientX);
      var W = w(), pos = W + (t0.clientX - pg.sx);
      if (pos < 0) pos = rubber(pos, W);
      if (pos > W) pos = W;
      x = pos;
      panel.style.translate = px(x);
      scrimAt(x);
    }, { passive: false });
    function pullEnd() {
      if (!pg || pg.mode !== 'pull') { pg = null; return; }
      pg = null;
      var v = ptr.velocity(), W = w();
      var open = project(x, v) < W * 0.5;
      if (open) {
        pendingV = v;
        cfg.pullOpen();
        if (!cfg.isOpen()) { pendingV = null; run(false, v); }
      } else {
        run(false, v);
      }
    }
    document.addEventListener('touchend', pullEnd, { passive: true });
    document.addEventListener('touchcancel', pullEnd, { passive: true });
  }

  /* ── Height on a spring (desktop search shelf) ── */
  var heights = typeof WeakMap === 'function' ? new WeakMap() : null;
  function height(el, px) {
    if (reduce || !heights) { el.style.height = px + 'px'; return; }
    var st = heights.get(el) || { anim: null, h: el.getBoundingClientRect().height };
    heights.set(el, st);
    setImp(el, 'transition', 'opacity 0.24s ease');
    if (st.anim) st.anim.stop();
    st.anim = spring({ from: st.h, to: px, velocity: st.anim ? st.anim.velocity : 0, preset: 'smooth',
      onUpdate: function (v) { st.h = v; el.style.height = Math.max(0, v) + 'px'; } });
  }

  window.RWPhysics = { rubber: rubber, spring: spring, gallery: gallery, sheet: sheet, height: height, presets: PRESETS };

  function wire() {
    var menu = document.querySelector('.rw-drawer[data-drawer]');
    if (menu) sheet({
      root: menu, panel: menu,
      isOpen: function () { return menu.classList.contains('rw-drawer--open'); },
      closeBtn: function () { return menu.querySelector('[data-drawer-close]'); },
      scrim: document.querySelector('[data-drawer-overlay]'),
      scrimDisplay: 'block'
    });
    var cd = document.querySelector('.rw-cd');
    var cdPanel = cd && cd.querySelector('.rw-cd__panel');
    if (cdPanel) sheet({
      root: cd, panel: cdPanel, visibilityEl: cd,
      isOpen: function () { return cd.classList.contains('is-open'); },
      closeBtn: function () { return cd.querySelector('.rw-cd__x[data-cart-drawer-close]'); },
      scrim: cd.querySelector('.rw-cd__scrim'),
            pullOpen: function () { if (window.RWCart && window.RWCart.open) window.RWCart.open(); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
})();
